import React from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { StrategyCardData } from '../../data/skillStrategyCards';

type StrategyCardProps = {
  card: StrategyCardData;
  onPersonalize?: () => void;
  personalizeDisabled?: boolean;
  compact?: boolean;
};

const StrategyCard: React.FC<StrategyCardProps> = ({
  card,
  onPersonalize,
  personalizeDisabled = false,
  compact = false,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: 'background.paper',
        border: '2px solid',
        borderColor: 'secondary.main',
        borderRadius: 2,
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        px: compact ? 2 : 2.5,
        py: compact ? 1.5 : 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 28, height: 28, borderRadius: '50%',
            bgcolor: theme.palette.secondary.main,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>
            K
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            Instant strategy · {card.skill}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
            {card.headline}
          </Typography>
        </Box>
      </Box>

      <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1.5 }}>
        {card.steps.map((step, i) => (
          <Typography
            key={i}
            component="li"
            variant="body2"
            sx={{ color: 'text.primary', mb: 0.75, lineHeight: 1.45 }}
          >
            {step}
          </Typography>
        ))}
      </Box>

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: card.commonTrap ? 1 : 1.5, fontStyle: 'italic' }}>
        When to use: {card.whenToUse}
      </Typography>

      {card.commonTrap && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          Watch out: {card.commonTrap}
        </Typography>
      )}

      {onPersonalize && (
        <Button
          variant="contained"
          size="small"
          disabled={personalizeDisabled}
          onClick={onPersonalize}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.secondary.dark}, ${theme.palette.primary.dark})`,
            },
          }}
        >
          Ask Kai to personalize this
        </Button>
      )}
    </Box>
  );
};

export default StrategyCard;
